import DAWLogo from '@/common/components/_misc/DAWLogo';
import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  FolderOpen as FolderOpenIcon,
  Refresh as RefreshIcon,
  CloudUpload as CommitIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import CommitDialog from './CommitDialog';
import ProjectChangesBanner from './ProjectChangesBanner';
import { useProjectCommit } from '../hooks/useProjectCommit';
import { useProjectDetails } from '../context/ProjectDetailsContext';
import { useProjectHeaderActions } from '../hooks/useProjectHeaderActions';
import { ItemActionsMenu } from '@/common/components/_misc/ItemActionsMenu';
import { useProjectPermissions } from '@/hooks/projectPermissions';
import { useEffect, useRef } from 'react';
import { OpenProjectInDAWButton } from './OpenProjectInDAWButton';
import { DAWType } from '@/types/daw';

interface ProjectHeaderProps {
  autoCommitMessage?: string | null;
  onAutoCommitStarted?: () => void;
}

const ProjectHeader: React.FC<ProjectHeaderProps> = ({
  autoCommitMessage,
  onAutoCommitStarted,
}) => {
  const submittedAutoCommit = useRef<string | null>(null);
  const {
    project,
    handleLocateProject,
    handleAutoLocateProject,
    handleOpenProject,
    primaryDAW,
    syncProjectDir,
    latestVersionFiles,
    filesWithSyncRef,
    refetchProject,
    onProjectUpdated,
  } = useProjectDetails();

  const { canUpload } = useProjectPermissions(project);

  // Use commit hook to handle all commit-related logic
  const {
    commitDialogOpen,
    initialCommitMessage,
    setCommitDialogOpen,
    fileStatuses,
    syncStatus,
    isRefreshing,
    handleRefreshStatus,
    handleCommit,
    handleOpenCommitDialog,
    handleCommitFromSavePrompt,
    isScanning,
    isCommitting,
    commitError,
    changeScan,
  } = useProjectCommit({
    project: project || null,
    projectDirHandle: syncProjectDir,
    latestVersionFiles,
    refetchProject,
    onProjectUpdated,
    filesWithSyncRef,
    backgroundScan: true,
  });

  useEffect(() => {
    if (!autoCommitMessage) {
      submittedAutoCommit.current = null;
      return;
    }
    if (
      !project ||
      !canUpload ||
      !syncProjectDir ||
      submittedAutoCommit.current === autoCommitMessage
    ) {
      return;
    }
    // Start only after mount effects settle. StrictMode replays mount effects;
    // starting a scan synchronously here lets cleanup invalidate it while the
    // submitted ref prevents the replacement effect from ever trying again.
    let cancelled = false;
    void Promise.resolve().then(() => {
      if (cancelled) return;
      submittedAutoCommit.current = autoCommitMessage;
      void handleCommitFromSavePrompt(autoCommitMessage);
      onAutoCommitStarted?.();
    });
    return () => {
      cancelled = true;
    };
  }, [
    autoCommitMessage,
    project,
    canUpload,
    handleCommitFromSavePrompt,
    onAutoCommitStarted,
    syncProjectDir,
  ]);

  const headerActions = useProjectHeaderActions();

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{ minHeight: 36, gap: 2, flexWrap: 'wrap' }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          {primaryDAW ? (
            <DAWLogo dawType={primaryDAW.type} variant="chip" size="medium" />
          ) : (
            // Show skeleton if we haven't determined the DAW yet but we do have version files (i.e. we're still loading)
            <Skeleton
              variant="rounded"
              width={200}
              height={36.75}
              sx={{ borderRadius: '5px' }}
            />
          )}
        </Box>

        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
          {!syncProjectDir ? (
            <>
              {handleAutoLocateProject && (
                <Tooltip title="Automatically search for the project folder">
                  <span>
                    {/* Mobile: icon only */}
                    <IconButton
                      aria-label="Automatically locate project folder"
                      onClick={handleAutoLocateProject}
                      size="small"
                      color="secondary"
                      sx={{ display: { xs: 'inline-flex', sm: 'none' } }}
                    >
                      <SearchIcon fontSize="small" />
                    </IconButton>
                    {/* Desktop: full button */}
                    <Button
                      startIcon={<SearchIcon />}
                      onClick={handleAutoLocateProject}
                      variant="outlined"
                      size="small"
                      color="secondary"
                      sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
                    >
                      Auto Locate
                    </Button>
                  </span>
                </Tooltip>
              )}
              <Tooltip title="Manually locate the project folder">
                <span>
                  {/* Mobile: icon only */}
                  <IconButton
                    aria-label="Choose project folder"
                    onClick={handleLocateProject}
                    size="small"
                    color="primary"
                    sx={{ display: { xs: 'inline-flex', sm: 'none' } }}
                  >
                    <FolderOpenIcon fontSize="small" />
                  </IconButton>
                  {/* Desktop: full button */}
                  <Button
                    startIcon={<FolderOpenIcon />}
                    onClick={handleLocateProject}
                    variant="outlined"
                    size="small"
                    color="primary"
                    sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
                  >
                    Locate Project
                  </Button>
                </span>
              </Tooltip>
            </>
          ) : (
            <>
              <Tooltip title="Refresh the status of the project">
                <span>
                  {/* Mobile: icon only */}
                  <IconButton
                    aria-label="Refresh project status"
                    onClick={handleRefreshStatus}
                    disabled={isRefreshing || syncStatus?.syncing}
                    size="small"
                    color="primary"
                    sx={{ display: { xs: 'inline-flex', sm: 'none' } }}
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                  {/* Desktop: full button */}
                  <Button
                    data-cy="refresh-status-button"
                    startIcon={<RefreshIcon />}
                    onClick={handleRefreshStatus}
                    disabled={isRefreshing || syncStatus?.syncing}
                    variant="outlined"
                    size="small"
                    color="primary"
                    sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
                  >
                    Refresh Status
                  </Button>
                </span>
              </Tooltip>

              {canUpload && (
                <Tooltip title="Create a new version of the project">
                  <span>
                    {/* Mobile: icon only */}
                    <IconButton
                      aria-label="Create project version"
                      onClick={handleOpenCommitDialog}
                      disabled={isRefreshing || syncStatus?.syncing}
                      size="small"
                      color="success"
                      sx={{ display: { xs: 'inline-flex', sm: 'none' } }}
                    >
                      <CommitIcon fontSize="small" />
                    </IconButton>
                    {/* Desktop: full button */}
                    <Button
                      data-cy="commit-button"
                      startIcon={<CommitIcon />}
                      onClick={handleOpenCommitDialog}
                      disabled={isRefreshing || syncStatus?.syncing}
                      variant="contained"
                      color="success"
                      size="small"
                      sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
                    >
                      New Version
                    </Button>
                  </span>
                </Tooltip>
              )}

              {handleOpenProject && primaryDAW && (
                <OpenProjectInDAWButton
                  onOpen={handleOpenProject}
                  label={`Open project in ${primaryDAW.type}`}
                  disabledReason={
                    primaryDAW.type === DAWType.STEMS ||
                    primaryDAW.type === DAWType.UNKNOWN
                      ? 'This project has no supported DAW project file to launch.'
                      : undefined
                  }
                />
              )}
              <ItemActionsMenu actions={headerActions} variant="header" />
            </>
          )}
        </Box>
      </Box>

      {changeScan && (
        <ProjectChangesBanner
          scan={changeScan}
          canUpload={canUpload}
          onCreate={handleOpenCommitDialog}
          busy={!!isCommitting}
        />
      )}
      {syncStatus?.syncing && !commitDialogOpen && (
        <Box sx={{ mt: -1, mb: 1 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 0.5 }}
          >
            {isCommitting
              ? `Creating New Version… ${syncStatus.progress}%`
              : 'Checking local changes…'}
          </Typography>
          <LinearProgress
            variant={isCommitting ? 'determinate' : 'indeterminate'}
            value={syncStatus.progress}
            sx={{
              height: 3,
              borderRadius: 1,
              backgroundColor: 'action.hover',
            }}
          />
        </Box>
      )}

      <CommitDialog
        open={commitDialogOpen}
        initialCommitMessage={initialCommitMessage}
        onClose={() => setCommitDialogOpen(false)}
        onCommit={handleCommit}
        fileStatuses={fileStatuses}
        isCommitting={!!isCommitting}
        isScanning={!!isScanning}
        error={commitError}
        onRetry={handleOpenCommitDialog}
      />
    </Box>
  );
};

export default ProjectHeader;
